import { action, computed, flow, observable } from 'mobx';
import { computedFn } from 'mobx-utils';

import iconSearch from '@/assets/icons/search-off-icn-sm.svg';
import { badgeStoreTabs } from '@/constants/badges';
import { LangID, langId, langNames } from '@/constants/lang';
import { localStorageKeys } from '@/constants/localStorage';
import { mapMissionTabs, missionTabs } from '@/constants/missions';
import { query } from '@/constants/query';
import { serviceTypes } from '@/constants/services';
import { AppError } from '@/services/rest/api/appError';
import { ServiceBase } from '@/services/rest/base';
import { MissionCheckResponse } from '@/types/api';
import { LangOption, StoreBadge, StoreMission } from '@/types/models';
import { ServicesMixin } from '@/utils/servicesMixin';

import BadgeStore from './BadgeStore';
import MissionsStore from './MissionsStore';
import ProfileStore from './ProfileStore';
import QueryStore from './QueryStore';

import { toastManager } from '.';

@ServicesMixin([serviceTypes.QUERY, serviceTypes.REST])
export default class AppStore {
  query: QueryStore;
  api: ServiceBase;

  profileStore: ProfileStore;
  badgesStore: BadgeStore;
  missionsStore: MissionsStore;

  @observable public accessor lang: LangID = langId.en;
  @observable public accessor langOptions: LangOption = {
    [langId.en]: {
      name: langNames.en,
      value: langId.en,
      enabled: false,
    },
    [langId.id]: {
      name: langNames.id,
      value: langId.id,
      enabled: false,
    },
  };

  @observable public accessor langReady = false;

  constructor(
    options: {
      missionsStore: MissionsStore;
      profileStore: ProfileStore;
      badgesStore: BadgeStore;
    },
    services?: [QueryStore, ServiceBase] | [],
  ) {
    const [queryService, restService] = services!;
    this.query = queryService!;
    this.api = restService!;

    this.profileStore = options.profileStore;
    this.badgesStore = options.badgesStore;
    this.missionsStore = options.missionsStore;
  }

  @action.bound init() {}

  @computed public get isReady() {
    const auth = this.query.get(query.auth);
    const sync = this.query.get(query.sync);
    const settings = this.query.get(query.settings);

    const isGameReady =
      auth.state.success &&
      sync.state.success &&
      settings.state.success &&
      this.profileStore.isReady &&
      this.langReady;

    return isGameReady;
  }

  @computed public get currentLang() {
    return (
      Object.values(this.langOptions).find(({ enabled }) => enabled) ||
      this.langOptions.en
    );
  }

  @action.bound public setLang(lang: LangID) {
    this.lang = lang;
    for (const key in this.langOptions) {
      this.langOptions[key as LangID].enabled = lang === key;
    }
    localStorage.setItem(localStorageKeys.lang, lang);
    this.setLangReady();
  }

  @action.bound public setLangReady() {
    this.langReady = true;
  }

  getStoreBadge = computedFn((externalId: number): StoreBadge => {
    const badge = this.badgesStore.badges[externalId];
    const userBadge = this.badgesStore.getUserBadge(externalId);
    const conditions = {
      keys: {
        available: badge.keysPrice <= (this.profileStore.user.keys ?? 0),
        missing: badge.keysPrice - (this.profileStore.user.keys ?? 0),
      },
    };

    return this.badgesStore.getStoreBadge({
      externalId,
      userBadge,
      conditions,
    });
  });

  storeBadges = computedFn((tab: keyof typeof badgeStoreTabs) => {
    if (tab === badgeStoreTabs.all) {
      return Object.values(this.badgesStore.badges).map((badge) =>
        this.getStoreBadge(badge.externalId),
      );
    }

    return (
      Object.values(this.badgesStore.badges)
        // .filter((badge) => badge.type === tab && badge.isActive)
        .map((badge) => this.getStoreBadge(badge.externalId))
    );
  });

  getStoreMission = computedFn((id: number): StoreMission => {
    const mission = this.missionsStore.getMission(id);
    const userMission = this.missionsStore.getUserMission(mission.externalId);

    return { mission, completed: Boolean(userMission) };
  });

  storeMissions = computedFn((tab: keyof typeof missionTabs) => {
    if (tab === missionTabs.all) {
      return Object.values(this.missionsStore.allMissions)
        .filter((mission) => mission.isActive)
        .map(({ id }) => this.getStoreMission(id))
        .sort(({ completed }) => (completed ? 1 : -1));
    }

    return Object.values(this.missionsStore.allMissions)
      .filter(
        (mission) =>
          mapMissionTabs[mission.duration] === tab && mission.isActive,
      )
      .map(({ id }) => this.getStoreMission(id))
      .sort(({ completed }) => (completed ? 1 : -1));
  });

  @flow.bound *checkMission(externalId: number) {
    try {
      const { mission }: MissionCheckResponse = yield this.query.withQuery(
        `mission-${externalId}`,
        () => this.api.checkMission(externalId),
      );

      // this.updatePlayer(player);
      this.missionsStore.setUserMission(mission);

      setTimeout(() => {
        this.missionsStore.setDialogMission(false);
        this.missionsStore.setDialogMissionComplete(true);
      }, 300);
    } catch (error) {
      if (error instanceof AppError) {
        toastManager.success(error.body.detail, iconSearch);
      } else {
        toastManager.error('An error has occurred during execution');
      }

      this.missionsStore.setDialogMission(false);
      console.error(error);
    }
  }

  @flow.bound *checkWallet(address?: string) {
    if (address === this.profileStore.user.wallet) return;

    if (
      this.profileStore.user.wallet &&
      address !== this.profileStore.user.wallet
    ) {
      toastManager.error('Your connected wallet is not the same as registered');

      throw Error('Your connected wallet is not the same as registered');
    }

    if (!this.profileStore.user.wallet && address) {
      try {
        yield this.api.setWallet(address);
        this.profileStore.updateUser({ wallet: address });
      } catch (error) {
        if (error instanceof AppError) {
          toastManager.success(error.body.detail, iconSearch);
        } else {
          toastManager.error('An error has occurred during execution');
        }

        throw error;
      }
    }
  }
}
